/**
 * `/match/parlor-requests` 진입의 광고 / 오가닉 판정.
 *
 * 이 경로의 실험은 **광고 유입과 오가닉 유입을 별도 실험 두 개**로 나눠 돌린다. 같은 처치를
 * 보더라도 두 유입의 기저 전환율 차이가 커서(#342, #343 실측 기준 12.6% vs 33.9%) 한 실험에
 * 섞으면 팔 간 구성비가 조금만 흔들려도 결과가 왜곡된다.
 *
 * `constants/parlor/costLeadFunnel.ts`(#345, #346)에 있던 것을 옮겼다. 그 실험은 A안 승리로
 * 종료돼 파일째 걷어낼 예정인데, 유입 판정 자체는 후속 실험(#347, #348)이 계속 쓴다.
 * 실험 상수와 같은 파일에 두면 정리할 때 같이 지워진다.
 */

/**
 * 광고 유입 세션 마킹 키.
 *
 * 광고 판정은 URL 파라미터로만 가능한데 새로고침·페이지 내 이동에서 유실된다. 마킹이 없으면
 * 같은 사람이 새로고침 한 번으로 오가닉 실험 모수로 넘어가 두 실험이 서로 오염된다.
 * 탭 단위(sessionStorage)라 탭을 닫으면 지워지고, 같은 탭에서의 왕복에는 값이 유지된다.
 *
 * 키 이름이 `parlor_cost_ad_entry` 로 남은 것은 #345, #346 때 심은 마킹을 들고 있는 세션이
 * 그대로 이어지게 하려는 것이다. 이름을 바꾸면 그 세션들이 전부 오가닉으로 떨어진다.
 */
const AD_ENTRY_KEY = "parlor_cost_ad_entry";

/** 실험 모수를 가르는 유입 구분. 두 실험은 이 값으로 상호 배타가 된다. */
export type ParlorRequestAdEntry = "ad" | "organic";

/**
 * 광고 파라미터 존재 여부. 기존 코드베이스 idiom 그대로 재사용한다.
 * 네이버 검색광고는 `n_query`/`n_ad`, 구글은 `gclid`. 다음/카카오 광고는 미포함.
 *
 * @param query router.query
 */
export const hasAdEntryParams = (query: Record<string, unknown>): boolean =>
  "n_query" in query || "n_ad" in query || "gclid" in query;

const isAdEntrySession = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(AD_ENTRY_KEY) !== null;
  } catch {
    return false;
  }
};

/**
 * 광고 유입을 세션에 남긴다. **effect 에서 호출할 것** (렌더 중 쓰기 금지).
 *
 * 판정 자체(`resolveParlorRequestAdEntry`)는 이 마킹 없이도 같은 렌더에서 성립한다.
 * 마킹이 필요한 건 파라미터가 사라진 **다음** 렌더·새로고침·페이지 내 이동이다.
 * 로그인 화면(`/login`)에는 광고 파라미터가 따라가지 않으므로 거기서의 판정은 전적으로
 * 이 마킹에 의존한다.
 *
 * @param query router.query
 */
export const markParlorRequestAdEntry = (query: Record<string, unknown>) => {
  if (typeof window === "undefined") return;
  if (!hasAdEntryParams(query)) return;
  try {
    sessionStorage.setItem(AD_ENTRY_KEY, "1");
  } catch {
    // 사파리 프라이빗 등 쓰기 실패. 마킹이 없으면 파라미터가 살아있는 동안만 광고로 잡힌다
  }
};

/**
 * 유입 구분 확정 (읽기 전용).
 *
 * 오가닉 실험 모수는 "광고 파라미터가 없는 전부"다. 오가닉 검색·다이렉트·장례식장 상세에서의
 * 내부 이동이 모두 포함된다. referrer 로 검색엔진만 골라내는 방식은 인앱 브라우저·프라이버시
 * 설정에서 유실이 잦아 모수가 불안정해진다.
 *
 * 한계: 오가닉으로 먼저 들어와 오가닉 실험에 등록된 뒤 같은 탭에서 광고로 재진입하면 광고
 * 실험에도 등록된다. 유입 기준 분리 실험의 본질적 한계라 감수한다.
 *
 * @param query router.query (`router.isReady` 이후에 호출할 것)
 */
export const resolveParlorRequestAdEntry = (query: Record<string, unknown>): ParlorRequestAdEntry =>
  hasAdEntryParams(query) || isAdEntrySession() ? "ad" : "organic";
